import { productTypes } from '../../../utils/productHelpers';
import { convertProductTermToMonths, sortArrayByObjectProperty } from '../../../utils/helpers';

/**
 * Sort accounts by product type to match dashboard ordering (matching old Dashboard logic):
 * 
 * Active accounts sorted in this order:
 * 1. Fixed Term (FRS) with maturity instruction window OPEN - sorted by maturity close date, then by term length
 * 2. Fixed Rate Cash ISA (FISA) with maturity instruction window OPEN - sorted by maturity close date, then by term length  
 * 3. Easy Access (OFS) accounts
 * 4. Notice Saver (NS) accounts
 * 5. Fixed Term (FRS) with maturity instruction window NOT open - sorted by term length
 * 6. Fixed Rate Cash ISA (FISA) with maturity instruction window NOT open - sorted by term length
 * 7. Other account types
 * 
 * Inactive accounts (in same product type order):
 * 1. Easy Access (OFS) closed
 * 2. Notice Saver (NS) closed
 * 3. Fixed Term (FRS) closed - sorted by term length
 * 4. Fixed Rate Cash ISA (FISA) closed - sorted by term length
 * 
 * @param {any[]} accounts - Array of account objects
 * @returns {{ activeAccounts: any[], closedAccounts: any[], orderedActiveAccounts: any[] }}
 */
export function sortAccounts(accounts = []) {
    const inactiveAccounts = accounts.filter((/** @type {any} */ acc) => acc.accountStatus === 'Closed');
    const activeAccounts = accounts.filter((/** @type {any} */ acc) => acc.accountStatus !== 'Closed');

    // Fixed Rate Saver (FRS) accounts with Instruction Window Open
    const frsActiveAccountsIWOpen = sortArrayByObjectProperty(
        activeAccounts.filter((/** @type {any} */ acc) => 
            acc.product.productType === productTypes.FixedTerm &&
            acc.maturity && 
            acc.maturity.maturityInstructionWindowStatus === 'InstructionOpen'
        ),
        ['maturity', 'maturityWindowCloseDate'],
        Date.parse
    ).sort((/** @type {any} */ a, /** @type {any} */ b) => 
        convertProductTermToMonths(a.product.productTerm) - convertProductTermToMonths(b.product.productTerm)
    );

    // Fixed Rate Cash ISA (FISA) accounts with Instruction Window Open
    const fisaActiveAccountsIWOpen = sortArrayByObjectProperty(
        activeAccounts.filter((/** @type {any} */ acc) => 
            acc.product.productType === productTypes.FixedRateCashISA &&
            acc.maturity && 
            acc.maturity.maturityInstructionWindowStatus === 'InstructionOpen'
        ),
        ['maturity', 'maturityWindowCloseDate'],
        Date.parse
    ).sort((/** @type {any} */ a, /** @type {any} */ b) => 
        convertProductTermToMonths(a.product.productTerm) - convertProductTermToMonths(b.product.productTerm)
    );

    // Easy Access (OFS) active accounts
    const ofsActiveAccounts = activeAccounts.filter((/** @type {any} */ acc) => 
        acc.product.productType === productTypes.EasyAccess ||
        acc.product.productType === productTypes.InstantAccessCashISA
    );

    // Notice Saver (NS) active accounts
    const nsActiveAccounts = activeAccounts.filter((/** @type {any} */ acc) => 
        acc.product.productType === productTypes.Notice
    );

    // Fixed Rate Saver (FRS) accounts with Instruction Window NOT Open
    const frsActiveAccountsIWNotOpen = activeAccounts
        .filter((/** @type {any} */ acc) => 
            acc.product.productType === productTypes.FixedTerm && 
            acc.maturity && 
            acc.maturity.maturityInstructionWindowStatus !== 'InstructionOpen'
        )
        .sort((/** @type {any} */ a, /** @type {any} */ b) => 
            convertProductTermToMonths(a.product.productTerm) - convertProductTermToMonths(b.product.productTerm)
        );

    // Fixed Rate Cash ISA (FISA) accounts with Instruction Window NOT Open
    const fisaActiveAccountsIWNotOpen = activeAccounts
        .filter((/** @type {any} */ acc) => 
            acc.product.productType === productTypes.FixedRateCashISA && 
            acc.maturity && 
            acc.maturity.maturityInstructionWindowStatus !== 'InstructionOpen'
        )
        .sort((/** @type {any} */ a, /** @type {any} */ b) => 
            convertProductTermToMonths(a.product.productTerm) - convertProductTermToMonths(b.product.productTerm)
        );

    // Other account types (catch-all)
    const restActiveAccounts = activeAccounts.filter((/** @type {any} */ acc) => 
        acc.product.productType !== productTypes.EasyAccess && 
        acc.product.productType !== productTypes.Notice && 
        acc.product.productType !== productTypes.FixedTerm && 
        acc.product.productType !== productTypes.FixedRateCashISA &&
        acc.product.productType !== productTypes.InstantAccessCashISA
    );

    // Inactive/Closed accounts - sorted by type
    const ofsInactiveAccounts = inactiveAccounts.filter((/** @type {any} */ acc) => 
        acc.product.productType === productTypes.EasyAccess ||
        acc.product.productType === productTypes.InstantAccessCashISA
    );
    
    const nsInactiveAccounts = inactiveAccounts.filter((/** @type {any} */ acc) => 
        acc.product.productType === productTypes.Notice
    );
    
    const frsInactiveAccounts = inactiveAccounts
        .filter((/** @type {any} */ acc) => acc.product.productType === productTypes.FixedTerm)
        .sort((/** @type {any} */ a, /** @type {any} */ b) => 
            convertProductTermToMonths(a.product.productTerm) - convertProductTermToMonths(b.product.productTerm)
        );
    
    const fisaInactiveAccounts = inactiveAccounts
        .filter((/** @type {any} */ acc) => acc.product.productType === productTypes.FixedRateCashISA)
        .sort((/** @type {any} */ a, /** @type {any} */ b) => 
            convertProductTermToMonths(a.product.productTerm) - convertProductTermToMonths(b.product.productTerm)
        );

    const orderedActiveAccounts = [
        ...frsActiveAccountsIWOpen,
        ...fisaActiveAccountsIWOpen,
        ...ofsActiveAccounts,
        ...nsActiveAccounts,
        ...frsActiveAccountsIWNotOpen,
        ...fisaActiveAccountsIWNotOpen,
        ...restActiveAccounts
    ];

    const closedAccounts = [
        ...ofsInactiveAccounts,
        ...nsInactiveAccounts,
        ...frsInactiveAccounts,
        ...fisaInactiveAccounts
    ];

    return {
        activeAccounts,
        closedAccounts,
        orderedActiveAccounts
    };
}

/**
 * Check if an account is rolling over
 * @param {any} account - Account object
 * @returns {boolean}
 */
export function isAccountRollingOver(account) {
    return account.maturity &&
        account.maturity.maturityInstruction &&
        account.maturity.maturityInstruction.includes('Rollover') &&
        account.accountStatus !== 'Closed';
}
